
import React from 'react';
import { visibilityBand } from '@/utils/visibilityBands';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Moon, Clock, Camera, Calendar } from 'lucide-react';
import { getMoonPhaseEmoji } from '@/utils/moonCalculations';

interface DayData {
  date: number;
  fullDate: string;
  galacticCoreStart: string;
  galacticCoreEnd: string;
  moonPhase: number;
  moonPhaseCycle?: number;
  notVisibleReason?: string;
}

interface BestNightsProps {
  monthData: DayData[];
  location: { name: string; latitude: number; longitude: number };
  month: number; // 0-indexed month (0 = January, 11 = December)
  year: number;
  selectedDay?: number;
  onDaySelect?: (dayDate: number) => void;
}

const BestNights: React.FC<BestNightsProps> = ({ monthData, location, month, year, selectedDay, onDaySelect }) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getVisibilityRating = (day: DayData) => {
    const moonPercent = Math.round(day.moonPhase * 100);
    
    // Check if galactic core is actually visible
    if (day.galacticCoreStart === 'Not visible' || 
        day.galacticCoreStart === 'No twilight' || 
        day.galacticCoreStart === 'Below horizon' ||
        day.galacticCoreStart === '--:--' ||
        day.galacticCoreStart === 'Not dark enough' ||
        day.galacticCoreStart === 'Too brief') {
      return 0;
    }
    
    // Rating based on moon phase when core is visible
    if (moonPercent <= 5) return 10;
    else if (moonPercent <= 10) return 9;
    else if (moonPercent <= 15) return 8;
    else if (moonPercent <= 20) return 7;
    else if (moonPercent <= 25) return 6;
    else if (moonPercent <= 35) return 5;
    else if (moonPercent <= 45) return 4;
    else if (moonPercent <= 55) return 3;
    else if (moonPercent <= 70) return 2;
    else if (moonPercent <= 85) return 1;
    else return 0;
  };

  const getQualityLabel = (rating: number) => {
    // Colours stay local; the label comes from the one ladder.
    const label = visibilityBand(rating);
    if (rating >= 8) return { label, color: 'bg-green-500' };
    if (rating >= 6) return { label, color: 'bg-gold' };
    if (rating >= 4) return { label, color: 'bg-yellow-500' };
    if (rating >= 2) return { label, color: 'bg-orange-500' };
    return { label: 'Poor', color: 'bg-red-500' };
  };

  const bestNights = monthData
    .map(day => ({
      ...day,
      rating: getVisibilityRating(day)
    }))
    .filter(day => day.rating > 0)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 5);

  const VisibilityStars = ({ rating }: { rating: number }) => {
    return (
      <div className="flex gap-1">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < Math.ceil(rating / 2) 
                ? 'fill-galaxy-blue text-galaxy-blue' 
                : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  if (bestNights.length === 0) {
    return (
      <Card className="glass-card border-border/50 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center text-foreground font-light text-xl">
            <Camera className="mr-3 text-galaxy-blue w-6 h-6" />
            Best Nights for Photography
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <div className="text-muted-foreground">
            <Moon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg mb-2">Limited visibility this month</p>
            <p className="text-sm">
              The galactic core has limited visibility during dark hours this month at your location. This could be due to the core being below the horizon during nighttime hours or other astronomical factors.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-border/50 shadow-lg">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-foreground font-light text-lg">
          <Camera className="mr-2 text-galaxy-blue w-5 h-5" />
          Best Nights
          <Badge variant="outline" className="ml-2 text-xs text-galaxy-blue border-galaxy-blue">
            {monthNames[month]} {year}
          </Badge>
        </CardTitle>
        {onDaySelect && (
          <p className="text-muted-foreground text-xs">
            Click a night to see the core path on the map
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {bestNights.map((night, index) => {
          const quality = getQualityLabel(night.rating);
          const moonPercent = Math.round(night.moonPhase * 100);

          return (
            <div key={night.date} className="relative">
              <div
                className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${
                  selectedDay === night.date
                    ? 'bg-gold/10 border-gold/30 ring-1 ring-gold/30'
                    : 'bg-secondary/20 border-border/30 hover:bg-secondary/30'
                } ${onDaySelect ? 'cursor-pointer' : ''}`}
                onClick={() => onDaySelect?.(selectedDay === night.date ? -1 : night.date)}
              >
                <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                  <div className="text-sm md:text-lg font-bold text-galaxy-blue flex-shrink-0 w-6 text-center">
                    #{index + 1}
                  </div>

                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="font-semibold text-foreground text-sm truncate">
                      {night.fullDate}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {night.galacticCoreStart} - {night.galacticCoreEnd}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                  <div className="hidden sm:block">
                    <VisibilityStars rating={night.rating} />
                  </div>
                  <div className="text-center flex-shrink-0">
                    <div className="text-lg">{getMoonPhaseEmoji(night.moonPhase, night.moonPhaseCycle)}</div>
                    <div className="text-[10px] text-muted-foreground">{moonPercent}%</div>
                  </div>

                  <Badge className={`${quality.color} text-white text-xs flex-shrink-0 hidden sm:inline-flex`}>
                    {quality.label}
                  </Badge>

                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-galaxy-blue text-sm">
                      {night.rating}/10
                    </div>
                  </div>
                </div>
              </div>

              {index === 0 && night.rating >= 8 && (
                <div className="absolute -top-1.5 -right-1.5">
                  <div className="bg-yellow-500 text-white px-1.5 py-0.5 rounded-full text-[10px] font-semibold shadow-md">
                    Best
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default BestNights;
